import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ChartLineUp } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1678572823447-45fc146df43c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    if (!username || !password) {
      toast.error("Bitte E-Mail (oder Benutzername) und Passwort eingeben.");
      return;
    }
    setBusy(true);
    try {
      const u = await login(username.trim(), password);
      toast.success("Anmeldung erfolgreich");
      if (u?.must_change_password) {
        nav("/passwort-aendern", { replace: true });
      } else {
        nav("/app");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Anmeldung fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Anmeldung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="Labor" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#002FA7]/30 mix-blend-multiply" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-sm bg-white/90 flex items-center justify-center">
              <ChartLineUp size={20} weight="bold" color="#002FA7" />
            </div>
            <span className="font-heading font-bold text-lg">NutriCalc</span>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] mb-3 opacity-80">Lab Edition</div>
            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold leading-[1.05] tracking-tight">
              Präzise Nährwerte.<br />Für die Industrie.
            </h1>
            <p className="mt-4 text-sm max-w-md opacity-90 leading-relaxed">
              Vollständige EU-Nährwertdeklaration nach VO 1169/2011 — von Brennwert bis Spurenelementen.
              Mit Rezeptverwaltung, Audit-Trail und Export als PDF & Excel.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-sm bg-[#002FA7] flex items-center justify-center">
              <ChartLineUp size={18} weight="bold" color="white" />
            </div>
            <span className="font-heading font-bold">NutriCalc</span>
          </div>

          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Anmeldung</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-1">Willkommen zurück</h2>
          <p className="text-sm text-zinc-500 mb-8">Melde dich an, um deine Rezepte zu verwalten.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-wider text-zinc-600">E-Mail oder Benutzername</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="z. B. anna"
                autoComplete="username"
                className="mt-1 rounded-sm"
                data-testid="login-username-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-600">Passwort</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="mt-1 rounded-sm"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-[#002FA7] hover:bg-[#00248a] rounded-sm h-10"
              data-testid="login-submit-button"
            >
              {busy ? "Anmelden…" : "Anmelden"}
            </Button>
          </form>
          <p className="text-xs text-zinc-500 text-center mt-6">
            Noch kein Konto? {" "}
            <Link to="/registrieren" className="text-[#002FA7] font-medium hover:underline" data-testid="login-to-register">
              Kostenlos registrieren
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
